import { useRef, useState, type DragEvent } from "react";
import {
  DocumentRow,
  type LibraryDocumentView,
} from "@/documents/DocumentRow";
import { OpenPdfDialog } from "@/documents/OpenPdfDialog";
import { usePresence } from "@/shared/use-presence";

type Props = {
  documents: LibraryDocumentView[];
  loading: boolean;
  busy: boolean;
  keepPdfByDefault: boolean;
  notice?: { tone: string; message: string } | null;
  onOpenFile(file: File, keepPdf: boolean): void;
  onOpenStored(document: LibraryDocumentView): void;
  onLocate(document: LibraryDocumentView, file: File): void;
  onExport(document: LibraryDocumentView): void;
  onRemoveLocalCopy(document: LibraryDocumentView): void;
  onImport(file: File): void;
  onDelete(document: LibraryDocumentView): void;
};

export function LibraryScreen({
  documents,
  loading,
  busy,
  keepPdfByDefault,
  notice,
  onOpenFile,
  onOpenStored,
  onLocate,
  onExport,
  onRemoveLocalCopy,
  onImport,
  onDelete,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const locateInput = useRef<HTMLInputElement>(null);
  const pendingLocate = useRef<LibraryDocumentView | null>(null);
  const [dragging, setDragging] = useState(false);
  const dropPresence = usePresence(dragging, 180);

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files.item(0);
    if (!file) return;
    if (file.name.endsWith(".marginalia.json")) {
      onImport(file);
    } else {
      onOpenFile(file, keepPdfByDefault);
    }
  };

  return (
    <main
      className={`library-shell app-screen ${dragging ? "is-dragging" : ""}`}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) setDragging(false);
      }}
      onDrop={handleDrop}
    >
      <div className="library-controls">
        <button
          className="primary-button compact"
          type="button"
          onClick={() => setDialogOpen(true)}
        >
          Add PDF
        </button>
      </div>

      {notice ? (
        <div className={`notice notice-${notice.tone}`} role="status">
          {notice.message}
        </div>
      ) : null}

      <section className="library-list-section" aria-label="PDFs">

        {loading ? (
          <div className="library-loading" role="status">
            Reading the local library…
          </div>
        ) : documents.length ? (
          <div className="document-list">
            {documents.map((document) => (
              <DocumentRow
                key={document.id}
                document={document}
                busy={busy}
                onOpen={onOpenStored}
                onLocate={(item) => {
                  pendingLocate.current = item;
                  locateInput.current?.click();
                }}
                onExport={onExport}
                onRemoveLocalCopy={onRemoveLocalCopy}
                onDelete={onDelete}
              />
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="empty-library"
            onClick={() => setDialogOpen(true)}
          >
            <strong>Add PDF</strong>
            <span>Choose a file or drop a PDF anywhere on this page.</span>
          </button>
        )}
      </section>


      {dropPresence.present ? (
        <div
          className="global-drop-indicator"
          data-motion-state={dropPresence.state}
          aria-hidden="true"
        >
          Drop to open locally
        </div>
      ) : null}

      <input
        ref={locateInput}
        hidden
        type="file"
        accept=".pdf,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.item(0);
          const document = pendingLocate.current;
          if (file && document) onLocate(document, file);
          event.currentTarget.value = "";
          pendingLocate.current = null;
        }}
      />
      <OpenPdfDialog
        open={dialogOpen}
        keepByDefault={keepPdfByDefault}
        busy={busy}
        onCancel={() => setDialogOpen(false)}
        onOpen={(file, keepPdf) => {
          onOpenFile(file, keepPdf);
          setDialogOpen(false);
        }}
      />
    </main>
  );
}
